/**
 * MobileBottomNav
 *
 * Bottom navigation bar for mobile devices.
 * Shows quick access to adjacent locations and campus overview.
 */

import React from 'react';
import {
  Home,
  Map,
  Building2,
  Palette,
  FlaskConical,
  Factory,
  TrendingUp,
  Briefcase,
  Calendar,
} from 'lucide-react';
import { CampusLocation } from '../../../types/spatial';
import { useSpatialNavigation } from '../../../hooks/useSpatialNavigation';
import { getLocationConfig, getAdjacentLocations } from '../../../config/locationConfig';

export interface MobileBottomNavProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * Get icon component for a location
 */
function getLocationIcon(location: CampusLocation): React.FC<{ className?: string }> {
  switch (location) {
    case CampusLocation.CAMPUS_OVERVIEW:
      return Map;
    case CampusLocation.HEADQUARTERS:
      return Building2;
    case CampusLocation.DESIGN_STUDIO:
      return Palette;
    case CampusLocation.RD_LAB:
      return FlaskConical;
    case CampusLocation.MANUFACTURING:
      return Factory;
    case CampusLocation.SALES_OFFICE:
      return TrendingUp;
    case CampusLocation.EXECUTIVE_OFFICE:
      return Briefcase;
    case CampusLocation.COMPANY_ARCHIVES:
      return Calendar;
    default:
      return Home;
  }
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  className = '',
}) => {
  const { currentLocation, navigateTo, goToCampus, isUnlocked, isAtCampus } =
    useSpatialNavigation();

  // Get adjacent locations to show in nav
  const adjacentLocations = getAdjacentLocations(currentLocation);

  // Filter to unlocked and limit to 4 items (plus campus button)
  const navItems = adjacentLocations
    .filter((loc) => isUnlocked(loc))
    .slice(0, 4);

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-50
                  bg-theme-bg-primary border-t border-theme-border
                  safe-area-bottom ${className}`}
      aria-label="Mobile navigation"
    >
      <div className="flex justify-around items-center py-2 px-4">
        {/* Adjacent location buttons */}
        {navItems.map((location) => {
          const config = getLocationConfig(location);
          const Icon = getLocationIcon(location);
          const isCurrent = location === currentLocation;

          return (
            <button
              key={location}
              onClick={() => navigateTo(location)}
              disabled={isCurrent}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg
                         transition-colors ${
                           isCurrent
                             ? 'text-theme-accent bg-theme-accent/10'
                             : 'text-theme-text-secondary hover:text-theme-text-primary'
                         }`}
              aria-label={config.name}
              aria-current={isCurrent ? 'page' : undefined}
            >
              <Icon className="w-5 h-5" />
              <span className="text-xs font-medium truncate max-w-[64px]">
                {config.name.split(' ')[0]}
              </span>
            </button>
          );
        })}

        {/* Campus Overview Button */}
        <button
          onClick={goToCampus}
          className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg
                     transition-colors ${
                       isAtCampus
                         ? 'text-theme-accent bg-theme-accent/10'
                         : 'text-theme-text-secondary hover:text-theme-text-primary'
                     }`}
          aria-label="Campus overview"
          aria-current={isAtCampus ? 'page' : undefined}
        >
          <Map className="w-5 h-5" />
          <span className="text-xs font-medium">Campus</span>
        </button>
      </div>
    </nav>
  );
};

export default MobileBottomNav;
